/**
 * Dedicated PDF exporter for the CanniComply Compliance Dossier.
 *
 * Mirrors the pattern used by exportCertificatePDF.js:
 *   1. Mount a fresh copy of the on-screen dossier offscreen at real A4
 *      portrait pixel dimensions so every page renders at its final size.
 *   2. Wait for fonts and one paint frame.
 *   3. Rasterise each page individually with html2canvas at 3× DPI.
 *   4. Compose the final jsPDF using one A4 portrait page per rasterised
 *      dossier page.
 *
 * Result: the downloaded PDF is visually identical to the on-screen
 * preview, regardless of the caller's viewport size.
 */

import { createRoot } from "react-dom/client";
import { StrictMode } from "react";
// html2canvas-pro is a maintained fork of html2canvas 1.4.1 that fixes the
// text-baseline offset bug — the previous version painted Manrope with a
// systematically-shifted baseline which clipped ascenders inside any
// `overflow: hidden` (i.e. `truncate`) container. The public API is
// identical so this is a drop-in swap.
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import ComplianceDossier, { DOSSIER_PAGE_PX } from "./ComplianceDossier";
import { getInlinedFontsCss, injectInlinedFonts, ensureFontsLoaded } from "./inlineFonts";

export async function exportDossierPDF(props, filename = "compliance-dossier.pdf") {
  // Before the stage mounts, not after. The dossier decides its own page
  // breaks from measured content heights as soon as it renders. If the webfonts
  // have not landed yet (cold cache / first visit) it measures against fallback
  // metrics and splits the pages in the wrong places, which is why the PDF can
  // come out disordered on a machine that has never loaded the site while
  // looking correct on one that has.
  await ensureFontsLoaded();

  // Offscreen stage — positioned way off-screen but still in the layout tree
  // so html2canvas can measure it correctly.
  const stage = document.createElement("div");
  // Kept within the viewport (behind everything, non-interactive) rather than
  // parked at -100000px: html2canvas derives its capture box from viewport
  // coordinates, and extreme offsets are a known source of cropped output.
  stage.style.position = "fixed";
  stage.style.left = "0";
  stage.style.top = "0";
  stage.style.zIndex = "-9999";
  stage.style.width = `${DOSSIER_PAGE_PX.width}px`;
  stage.style.pointerEvents = "none";
  stage.style.background = "#ffffff";
  stage.setAttribute("data-dossier-stage", "1");
  document.body.appendChild(stage);

  const root = createRoot(stage);
  root.render(
    <StrictMode>
      <ComplianceDossier {...props} />
    </StrictMode>
  );

  try {
    // Wait for fonts + two RAFs so text is measured post-layout.
    if (document.fonts && document.fonts.ready) {
      await document.fonts.ready;
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    // Preload the woff2 files as data URIs. html2canvas measures/paints in a
    // detached clone that does not reliably re-link /fonts.css, so we hand it
    // the font bytes inline via onclone.
    const inlinedFontsCss = await getInlinedFontsCss();

    const pages = Array.from(stage.querySelectorAll('[data-dossier-page="1"]'));
    if (pages.length === 0) throw new Error("Dossier produced no pages");

    const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
    const pageWidthMm = pdf.internal.pageSize.getWidth();
    const pageHeightMm = pdf.internal.pageSize.getHeight();

    for (let i = 0; i < pages.length; i += 1) {
      // windowWidth/windowHeight must describe the *window*, not the page.
      // Passing the A4 page size laid the clone out in a 794x1123 viewport
      // while the stage sat far off-screen, so html2canvas captured the wrong
      // region and every page came out cropped. Omit them and state the
      // capture box explicitly instead.
      const canvas = await html2canvas(pages[i], {
        scale: 3,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        width: DOSSIER_PAGE_PX.width,
        height: DOSSIER_PAGE_PX.height,
        scrollX: 0,
        scrollY: 0,
        onclone: (clonedDoc) => injectInlinedFonts(clonedDoc, inlinedFontsCss),
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.94);
      if (i > 0) pdf.addPage();
      pdf.addImage(imgData, "JPEG", 0, 0, pageWidthMm, pageHeightMm, undefined, "FAST");
    }

    pdf.save(filename);
  } finally {
    // Always tear down the offscreen tree.
    try {
      root.unmount();
    } catch {
      /* noop */
    }
    document.body.removeChild(stage);
  }
}
